import map from 'lodash/map';
import filter from 'lodash/filter';
import find from 'lodash/find';
import React, {Component, Fragment} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Alert from '@spothero/ui/alert';
import Form from '@spothero/ui/form';
import FormGroup from '@spothero/ui/form-group';
import FormGroupError from '@spothero/ui/form-group-error';
import Label from '@spothero/ui/label';
import Radio from '@spothero/ui/radio';
import RadioGroup from '@spothero/ui/radio-group';
import Tooltip from '@spothero/ui/tooltip';
import EnvironmentUtils from '@spothero/utils/environment';
import AddNewLicensePlateForm from './AddNewLicensePlateForm';

export default class LicensePlateSelect extends Component {
    static propTypes = {
        user: PropTypes.object,
        selectedLicensePlate: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
            PropTypes.object,
            PropTypes.bool
        ]),
        isParentVisible: PropTypes.bool,
        defaultToFirstOption: PropTypes.bool,
        showUnknownChoices: PropTypes.bool,
        allowNaming: PropTypes.bool,
        showTowAlert: PropTypes.bool,
        newPlateFormErrors: PropTypes.array,
        onSelectChange: PropTypes.func
    };
    static defaultProps = {
        showUnknownChoices: true,
        allowNaming: true,
        showTowAlert: true
    };

    constructor(props) {
        super(props);

        const {
            user,
            selectedLicensePlate
        } = props;
        const {
            licensePlates,
            selectedPlate,
            isNewPlate
        } = this._getUserPlates(user, selectedLicensePlate);

        this._isMobile = EnvironmentUtils.isMobileDevice();

        this.state = {
            licensePlates,
            selectedLicensePlate: selectedPlate,
            newLicensePlate: isNewPlate,
            showWarningTooltip: false,
            requiredError: null,
            tipTrigger: null
        };
    }

    componentDidUpdate(prevProps, prevState) {
        if (!this._warningDismissed && prevState.tipTrigger !== this.state.tipTrigger) {
            this._showWarningTooltip();
        }
    }

    _onSelectedPlateChange = evt => {
        const {
            onSelectChange
        } = this.props;
        const value = evt.target.value;
        const newPlate = (value === 'new');
        const newState = {
            newLicensePlate: newPlate,
            selectedLicensePlate: (newPlate) ? false : value,
            requiredError: null,
            showWarningTooltip: false,
            tipTrigger: null
        };

        if (!this._warningDismissed && (value === 'DONTKNOW' || value === 'RENTAL')) {
            newState.tipTrigger = (value === 'DONTKNOW')
                ? '.LicensePlateSelect-dont-know'
                : '.LicensePlateSelect-rental';
        }

        this.setState(newState);

        if (onSelectChange) {
            onSelectChange(value);
        }
    }

    _onNewPlateInputChange = value => {
        this.setState({
            selectedLicensePlate: value
        });
    }

    _onWarningTooltipHidden = () => {
        this._warningDismissed = true;

        this.setState({
            showWarningTooltip: false
        });
    }

    _showWarningTooltip() {
        this.setState({
            showWarningTooltip: true
        });
    }

    _getUserPlates(user, selectedLicensePlate) {
        const {
            showUnknownChoices,
            defaultToFirstOption
        } = this.props;
        const userHasPlates = (user.licensePlates && user.licensePlates.length);
        const userDefaultPlate = (userHasPlates) ? find(user.licensePlates, 'isDefault') : null;
        let selectedPlate = selectedLicensePlate || (userDefaultPlate && userDefaultPlate.licensePlate);
        let isNewPlate = Boolean(!selectedPlate || selectedPlate === 'new');
        let plates = [
            {
                className: 'LicensePlateSelect-new',
                label: 'Add new license plate',
                value: 'new',
                checked: isNewPlate
            }
        ];

        if (showUnknownChoices) {
            plates = [
                {
                    className: 'LicensePlateSelect-dont-know',
                    label: `I don't know my license plate # right now`,
                    value: 'DONTKNOW',
                    checked: false
                },
                {
                    className: 'LicensePlateSelect-rental',
                    label: `Using a rental car`,
                    value: 'RENTAL',
                    checked: false
                },
                ...plates
            ];
        }

        if (userHasPlates) {
            const filteredUserPlates = filter(user.licensePlates, plate => {
                return plate.licensePlate !== 'RENTAL' && plate.licensePlate !== 'DONTKNOW';
            });

            plates = map(filteredUserPlates, ({licensePlate, name, isDefault}) => {
                if (isDefault && !selectedPlate) {
                    selectedPlate = licensePlate;
                }

                return {
                    label: licensePlate,
                    name,
                    value: licensePlate,
                    checked: (selectedPlate === licensePlate)
                };
            }).concat(plates);
        }

        if (defaultToFirstOption && filter(plates, {checked: true}).length === 0) {
            // there are no checked plates, this may happen if the user deleted their plate in their account and its
            // still showing up on parking pass or similar so we select the first one available
            plates[0].checked = true;
            selectedPlate = plates[0].value;
        }

        if (plates.length === 1 && plates[0].value === 'new') {
            selectedPlate = false;
            isNewPlate = true;
        }

        return {
            licensePlates: plates,
            selectedPlate,
            isNewPlate
        };
    }

    submit() {
        const {
            licensePlates,
            selectedLicensePlate,
            newLicensePlate
        } = this.state;
        const newPlateFormData = (newLicensePlate) ? this._addNewPlateForm.submit() : false;
        let newLicensePlateName = (newPlateFormData) ? newPlateFormData.plateName : false;

        if (!selectedLicensePlate) {
            if (!newLicensePlate) {
                this.setState({
                    requiredError: ['Please choose a license plate option.']
                });
            }

            return false;
        }

        if (!newPlateFormData && selectedLicensePlate !== 'DONTKNOW' && selectedLicensePlate !== 'RENTAL') {
            // grab the name of the selected plate if its not new or unknown so we can pass it along with the submit data
            const selectedPlateObj = find(licensePlates, {value: selectedLicensePlate});

            if (selectedPlateObj && selectedPlateObj.name) {
                newLicensePlateName = selectedPlateObj.name;
            }
        }

        this.setState({
            showWarningTooltip: false
        });

        return {
            selectedLicensePlate,
            newLicensePlate,
            newLicensePlateName,
            newLicensePlateIsDefault: (newPlateFormData) ? newPlateFormData.saveLicensePlateAsDefault : false
        };
    }

    render() {
        const {
            user,
            newPlateFormErrors,
            isParentVisible,
            allowNaming,
            showTowAlert,
            showUnknownChoices
        } = this.props;
        const {
            licensePlates,
            showWarningTooltip,
            requiredError,
            tipTrigger,
            newLicensePlate
        } = this.state;
        const formGroupClasses = classNames(
            {ContainsErrors: requiredError}
        );

        return (
            <Fragment>
                <Form
                    id="LicensePlateSelect-form"
                    className="LicensePlateSelect"
                >
                    <FormGroup className={formGroupClasses}>
                        {(showUnknownChoices || licensePlates.length > 1) &&
                            <Label>License Plate</Label>
                        }
                        {(showUnknownChoices || licensePlates.length > 1) && showTowAlert &&
                            <Alert type="highlight">Please confirm so you don&apos;t get towed.</Alert>
                        }
                        {(showUnknownChoices || licensePlates.length > 1) &&
                            <FormGroup>
                                <RadioGroup
                                    id="LicensePlateSelect-license-options"
                                    name="licensePlate"
                                    className="LicensePlateSelect-options"
                                    onChange={this._onSelectedPlateChange}
                                >
                                    {map(licensePlates, (plate, i) => {
                                        return (
                                            <Radio
                                                key={i}
                                                label={(
                                                    <Label className={plate.className}>
                                                        {plate.name &&
                                                            <span className="Radio-item-name">{plate.name}</span>
                                                        }
                                                        {plate.label}
                                                    </Label>
                                                )}
                                                value={plate.value}
                                                defaultChecked={plate.checked}
                                            />
                                        );
                                    })}
                                </RadioGroup>
                            </FormGroup>
                        }
                    </FormGroup>
                    {requiredError &&
                        <FormGroupError>{requiredError}</FormGroupError>
                    }
                </Form>
                {newLicensePlate &&
                    <AddNewLicensePlateForm
                        ref={node => { this._addNewPlateForm = node; }}
                        user={user}
                        formErrors={newPlateFormErrors}
                        showTowAlert={(!showUnknownChoices && licensePlates.length < 2) && showTowAlert}
                        onPlateNumberChanged={this._onNewPlateInputChange}
                        allowNaming={allowNaming}
                    />
                }
                {(isParentVisible && tipTrigger && showWarningTooltip) &&
                    <Tooltip
                        className="LicensePlateSelect-tooltip"
                        position={(this._isMobile) ? 'top' : 'right'}
                        triggerSelector={tipTrigger}
                        tipOffset={10}
                        onHidden={this._onWarningTooltipHidden}
                    >
                        Visit our app or &quot;My Account&quot; page to update your license plate # before you park so you don&apos;t get towed or fined.
                    </Tooltip>
                }
            </Fragment>
        );
    }
}
